<template>
  <div>
    <ClientSelection
      v-if="displayClientSelection"
      @closeClientSelection="closeClientSelectionHandler"
      @cancelSelection="cancelSelectionHandler"
    />
    <SecondaryHeader>
      <template #content>{{ $t('client') }} </template>
    </SecondaryHeader>
    <div class="atb-w-2/5 atb-ml-8 atb-pt-7">
      <Card>
        <template #header>
          <h1
            class="card-title"
            id="clientNameLabel"
          >
            {{ client.clientName }}
          </h1>
        </template>
        <template #body>
          <div class="atb-grid atb-grid-cols-3 atb-gap-8 atb-pl-4 atb-py-4">
            <div class="atb-col-span-1 label-text">
              {{ $t('description') }}:
            </div>
            <div
              class="atb-col-span-2"
              id="clientDescriptionLabel"
            >
              {{ client.description }}
            </div>
            <div class="atb-col-span-1 label-text">{{ $t('createdBy') }}:</div>
            <div
              class="atb-col-span-2"
              id="clientCreatedByLabel"
            >
              {{ client.createdBy }}
            </div>
            <div class="atb-col-span-1 label-text">{{ $t('createdOn') }}:</div>
            <div
              class="atb-col-span-2"
              id="clientCreatedOnLabel"
            >
              {{
                client.createdAt === 0
                  ? 'Not Specified'
                  : dayjs(client.createdAt).format('MMM DD YYYY HH:mm:ss')
              }}
            </div>
            <div class="atb-col-span-1 label-text">{{ $t('updatedBy') }}:</div>
            <div
              class="atb-col-span-2"
              id="clientUpdatedByLabel"
            >
              {{ client.updatedBy || 'Not Specified' }}
            </div>
            <div class="atb-col-span-1 label-text">{{ $t('updatedOn') }}:</div>
            <div
              class="atb-col-span-2"
              id="clientUpdatedOnLabel"
            >
              {{
                client.updatedAt === 0 || client.updatedAt === null
                  ? 'Not Specified'
                  : dayjs(client.updatedAt).format('MMM DD YYYY HH:mm:ss')
              }}
            </div>
          </div>
        </template>
        <template #footer>
          <div class="atb-grid atb-grid-cols-3 atb-gap-8 atb-pl-4 atb-py-4">
            <div class="atb-col-span-1 label-text">
              {{ $t('switchClient') }}:
            </div>
            <div class="atb-col-span-2">
              <Button
                id="clientChangeButton"
                icon-placement="left"
                @click="openClientSelection"
              >
                <template #icon>
                  <span class="material-icons">swap_horiz</span>
                </template>
                {{ $t('switchClient') }}
              </Button>
            </div>
            <div class="atb-col-span-1 label-text">
              {{ $t('launchNewTab') }}:
            </div>
            <div class="atb-col-span-2">
              <Button
                id="clientLaunchButton"
                icon-placement="left"
                @click="openClientSelectionInNewWindow"
              >
                <template #icon>
                  <span class="material-icons">open_in_new</span>
                </template>
                {{ $t('launchNewTab') }}
              </Button>
            </div>
          </div>
        </template>
      </Card>
    </div>
    <div class="datasources-table atb-bg-white atb-my-8 atb-mx-8 atb-pb-1">
      <div
        class="datasources-table-title atb-h-14 atb-p-4 atb-flex atb-align-center"
      >
        {{ $t('dataSources') }}
      </div>
      <div
        id="clientDatasourceGrid"
        class="table-wrap atb-w-full atb-h-48"
      >
        <DataGrid
          :column-defs="columnDefs"
          :is-serverside="false"
          :pagination="false"
          :row-data="rowData"
          :row-multi-select-with-click="false"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref } from 'vue';
  import DataGrid from '@/app/shared/components/data-grid/DataGrid.vue';
  import SecondaryHeader from '@atb-ui/eds-vue/src/components/SecondaryHeader.vue';
  import Card from '@atb-ui/eds-vue/src/components/Card.vue';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import { useClientStore } from '@/app/features/ams/client/client-store';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import dayjs from 'dayjs';
  import ClientSelection from '@/app/features/ams/client/ClientSelection.vue';

  const { t } = useI18n();
  const clientStore = useClientStore();
  const client = clientStore.getSelectedClient;
  const displayClientSelection = ref(false);

  const columnDefs = ref([
    {
      field: 'name',
      headerName: t('name'),
      suppressSizeToFit: true,
      sortable: true,
    },
    {
      field: 'description',
      headerName: t('description'),
      suppressSizeToFit: false,
    },
  ]);

  const router = useRouter();

  const rowData = computed(() => client.dataSources);

  const openClientSelection = async () => {
    await clientStore.fetchClientData();
    displayClientSelection.value = true;
  };

  const closeClientSelectionHandler = () => {
    displayClientSelection.value = false;
    router.push({ name: 'ams-home' });
  };

  const cancelSelectionHandler = () => {
    displayClientSelection.value = false;
  };

  const openClientSelectionInNewWindow = () => {
    const route = router.resolve({ path: '/ams/home' });
    window.open(route.href, '_blank');
  };
</script>

<style scoped>
  .label-text {
    color: #767676;
  }

  .datasources-table {
    border: 1px solid #d6d6d6;
    border-radius: 6px;
  }

  .datasources-table-title {
    font-weight: 700;
    font-size: 16px;
  }
</style>
